import {
  BoundDateField,
  BoundNumberField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  IconButton,
  TextField,
  useComputed,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import { PdfViewer } from "src/components/PdfViewer";
import {
  AssetInfoFragment,
  InputMaybe,
  LotAcqLotAcqPurchaseAgreementFragment,
  LotAcqLotFragment,
  LotAcqPurchaseAgreementFundOwnerEntitiesQuery,
  SaveLotAcqPurchaseAgreementInput,
  useDeleteLotAcqPurchaseAgreementMutation,
  useLotAcqPurchaseAgreementDocumentsQuery,
  useLotAcqPurchaseAgreementFundOwnerEntitiesQuery,
  useSaveLotAcqPurchaseAgreementMutation,
  useSendLotAcqPurchaseAgreementDepositCancelationForBookingMutation,
  useSendLotAcqPurchaseAgreementDepositForBookingMutation,
} from "src/generated/graphql-types";
import { PageHeader } from "src/routes/layout/PageHeader";
import { DocumentIdParams } from "src/routes/routesDef";
import { createLotDetailsUrl } from "src/RouteUrls";
import { DateOnly, ensureDateOnly } from "src/utils/dates";
import { queriesResult } from "src/utils/queryResult";

export function LotAcqPurchaseAgreementEditor() {
  const { documentId } = useParams<DocumentIdParams>();

  const documentsQuery = useLotAcqPurchaseAgreementDocumentsQuery({
    variables: { documentIds: [documentId] },
    skip: !documentId,
  });
  const fundOwnerEntitiesQuery = useLotAcqPurchaseAgreementFundOwnerEntitiesQuery();

  return queriesResult([documentsQuery, fundOwnerEntitiesQuery] as const, {
    data: (documentsData, fundOwnerEntitiesData) => {
      const document = documentsData?.documents[0];
      const lot = document.parent as LotAcqLotFragment;
      const documentId = document.id;
      const lapa = document.lotAcqPurchaseAgreement;
      return (
        <LotAcqPurchaseAgreementForm
          documentId={documentId}
          lot={lot}
          assetInfo={document.asset}
          lotAcqPurchaseAgreement={lapa!}
          fundOwnerEntities={fundOwnerEntitiesData?.fundOwnerEntities ?? []}
        />
      );
    },
  });
}

type LotAcqPurchaseAgreementEditorProps = {
  documentId: string;
  lot: LotAcqLotFragment;
  assetInfo: AssetInfoFragment;
  lotAcqPurchaseAgreement: LotAcqLotAcqPurchaseAgreementFragment;
  fundOwnerEntities: LotAcqPurchaseAgreementFundOwnerEntitiesQuery["fundOwnerEntities"];
};

function LotAcqPurchaseAgreementForm({
  documentId,
  lot,
  assetInfo,
  lotAcqPurchaseAgreement,
  fundOwnerEntities,
}: LotAcqPurchaseAgreementEditorProps) {
  const [saveLotAcqPurchaseAgreement] = useSaveLotAcqPurchaseAgreementMutation();
  const [deleteLotAcqPurchaseAgreement] = useDeleteLotAcqPurchaseAgreementMutation();
  const [sendDepositForBooking] = useSendLotAcqPurchaseAgreementDepositForBookingMutation();
  const [sendDepositCancelationForBooking] = useSendLotAcqPurchaseAgreementDepositCancelationForBookingMutation();
  const history = useHistory();

  // Once the deposit has been booked the agreement is locked for editing - the only
  // remaining action is to book the cancellation against the original deposit JE.
  const isDepositBooked = lotAcqPurchaseAgreement?.bookingDepositEventSent;

  const formState = useFormState({
    config: formConfig,
    init: {
      input: mapPurchaseAgreementToForm(lotAcqPurchaseAgreement),
    },
  });

  const isDirty = useComputed(() => formState.dirty, [formState]);

  const saveLotAcqPurchaseAgreementFunction = useCallback(
    async (formState: ObjectState<LotAcqPurchaseAgreementFormInput>) => {
      const result = await saveLotAcqPurchaseAgreement({
        variables: { input: mapToInput(formState.value) },
      });

      // Re-sync the form with what the backend persisted
      const saved = result.data?.saveLotAcqPurchaseAgreement?.lotAcqPurchaseAgreement;
      if (saved) {
        formState.set(mapPurchaseAgreementToForm(saved));
      }
      formState.commitChanges();
    },
    [saveLotAcqPurchaseAgreement],
  );

  function mapToInput(formStateValue: LotAcqPurchaseAgreementFormInput): SaveLotAcqPurchaseAgreementInput {
    const { documentId, lotId, depositPaidDate, depositCancelledDate, ...others } = formStateValue;
    return {
      ...others,
      documentId,
      lotId,
      depositPaidDate: depositPaidDate ? new DateOnly(depositPaidDate) : undefined,
      depositCancelledDate: depositCancelledDate ? new DateOnly(depositCancelledDate) : undefined,
    };
  }

  const title = "Edit Purchase Agreement for " + lot?.address?.street1 + " (" + lot?.id + ")";

  const depositBookingTooltip = lotAcqPurchaseAgreement?.canSendDepositForBooking.allowed
    ? "Books the Land Deposit to Accounting - cannot be undone"
    : lotAcqPurchaseAgreement?.canSendDepositForBooking.disabledReasons.map((reason) => reason.message).join(", ");

  const cancelationBookingTooltip = lotAcqPurchaseAgreement?.canSendDepositCancelationForBooking.allowed
    ? "Books the Land Deposit Cancellation to Accounting - cannot be undone"
    : lotAcqPurchaseAgreement?.canSendDepositCancelationForBooking.disabledReasons
        .map((reason) => reason.message)
        .join(", ");

  return (
    <FormLines compact gap={1}>
      <PageHeader title={title} backButton={createLotDetailsUrl(lot.id)} />
      <TextField
        label="Investment Status"
        value={lotAcqPurchaseAgreement?.lot.lotInvestmentStatus.name}
        readOnly
        onChange={() => {}}
      />
      <BoundNumberField
        field={formState.purchasePriceInCents}
        label="Purchase Price (from this document)"
        type="cents"
        readOnly={isDepositBooked}
      />
      <BoundNumberField
        field={formState.earnestMoneyInCents}
        label="Earnest Money"
        type="cents"
        readOnly={isDepositBooked}
      />
      <BoundNumberField field={formState.optionFeeInCents} label="Option Fee" type="cents" readOnly={isDepositBooked} />
      <BoundDateField field={formState.depositPaidDate} label="GL Deposit Date" readOnly={isDepositBooked} />
      <BoundDateField field={formState.depositCancelledDate} label="GL Cancel Date" disabled={!isDepositBooked} />
      <BoundNumberField
        field={formState.depositReturnedAmountInCents}
        label="Returned Amount"
        type="cents"
        disabled={!isDepositBooked}
        helperText="The deposit money that was returned to us."
      />
      {!lotAcqPurchaseAgreement.lot.fundOwnerEntity && (
        <BoundSelectField
          field={formState.overrideFundOwnerEntityId}
          label="Legal Entity for Booking"
          options={fundOwnerEntities}
          getOptionValue={(foe) => foe.id}
          getOptionLabel={(foe) => foe.name}
          readOnly={isDepositBooked}
          helperText="This lot has no Fund Owner Entity set, so pick the legal entity to book the deposit against."
        />
      )}
      <div css={Css.df.fdr.mt1.gap1.$}>
        <Button
          variant="primary"
          label="Save"
          disabled={!isDirty}
          onClick={async () => {
            await saveLotAcqPurchaseAgreementFunction(formState);
            formState.commitChanges();
          }}
        />
        {!isDepositBooked && (
          <>
            <Button
              variant="secondary"
              label="Book Deposit to Accounting"
              icon="finances"
              onClick={async () => {
                await sendDepositForBooking({ variables: { input: { id: lotAcqPurchaseAgreement.id } } });
                history.push({ pathname: createLotDetailsUrl(lot.id) });
              }}
              disabled={isDirty || !lotAcqPurchaseAgreement?.canSendDepositForBooking.allowed}
              tooltip={isDirty ? "Save your changes before booking" : depositBookingTooltip}
            />
            <IconButton
              icon="trash"
              tooltip="Delete Lot Acq Purchase Agreement"
              onClick={async () => {
                await deleteLotAcqPurchaseAgreement({
                  variables: { input: { id: lotAcqPurchaseAgreement.id } },
                });
                history.push({ pathname: createLotDetailsUrl(lot.id) });
              }}
            />
          </>
        )}
        {isDepositBooked && (
          <Button
            variant="secondary"
            label="Book Cancelation to Accounting"
            icon="finances"
            onClick={async () => {
              await sendDepositCancelationForBooking({ variables: { input: { id: lotAcqPurchaseAgreement.id } } });
              history.push({ pathname: createLotDetailsUrl(lot.id) });
            }}
            disabled={isDirty || !lotAcqPurchaseAgreement?.canSendDepositCancelationForBooking.allowed}
            tooltip={isDirty ? "Save your changes before booking" : cancelationBookingTooltip}
          />
        )}
      </div>
      <PdfViewer
        hasHeader
        title={"Uploaded Purchase Agreement PDF"}
        assets={[assetInfo]}
        pdfPageWidth={getDynamicPdfVierwerWidth()}
      />
    </FormLines>
  );
}

export type LotAcqPurchaseAgreementFormInput = {
  id: InputMaybe<string>;
  lotId: InputMaybe<string>;
  documentId: InputMaybe<string>;
  purchasePriceInCents: InputMaybe<number>;
  earnestMoneyInCents: InputMaybe<number>;
  optionFeeInCents: InputMaybe<number>;
  depositPaidDate: InputMaybe<Date>;
  depositCancelledDate: InputMaybe<Date>;
  depositReturnedAmountInCents: InputMaybe<number>;
  overrideFundOwnerEntityId: InputMaybe<string>;
};

export const formConfig: ObjectConfig<LotAcqPurchaseAgreementFormInput> = {
  id: { type: "value" },
  lotId: { type: "value", rules: [required] },
  documentId: { type: "value", rules: [required] },
  purchasePriceInCents: { type: "value", rules: [required] },
  earnestMoneyInCents: { type: "value", rules: [required] },
  optionFeeInCents: { type: "value", rules: [required] },
  // Dates are only relevant at booking time, so they are not required to save.
  depositPaidDate: { type: "value" },
  depositCancelledDate: { type: "value" },
  // Only set when the amount returned to us differs from the earnest money, so also not required.
  depositReturnedAmountInCents: { type: "value" },
  // Only set when the lot has no Fund Owner Entity set, so also not required.
  overrideFundOwnerEntityId: { type: "value" },
};

function mapPurchaseAgreementToForm(
  purchaseAgreement: LotAcqLotAcqPurchaseAgreementFragment,
): LotAcqPurchaseAgreementFormInput {
  return {
    id: purchaseAgreement.id,
    lotId: purchaseAgreement.lot.id,
    documentId: purchaseAgreement.document.id,
    purchasePriceInCents: purchaseAgreement.purchasePriceInCents,
    earnestMoneyInCents: purchaseAgreement.earnestMoneyInCents,
    optionFeeInCents: purchaseAgreement.optionFeeInCents,
    depositPaidDate: purchaseAgreement.depositPaidDate ? ensureDateOnly(purchaseAgreement.depositPaidDate) : undefined,
    depositCancelledDate: purchaseAgreement.depositCancelledDate
      ? ensureDateOnly(purchaseAgreement.depositCancelledDate)
      : undefined,
    depositReturnedAmountInCents: purchaseAgreement.depositReturnedAmountInCents,
    overrideFundOwnerEntityId: purchaseAgreement.overrideFundOwnerEntity?.id,
  };
}

export function getDynamicPdfVierwerWidth(browserWidth?: number) {
  const { innerHeight } = window;
  return innerHeight > 900 ? 820 : innerHeight > 800 ? 760 : 640;
}
